// Shared logging helpers route stable log ids through one registry-backed formatter.
// Browser and node consumers reuse the same sink model without duplicating messages.
import { resolveLentyLogEvent, type LentyLogEvent, type LentyLogChannel, type LentyLogScope, type LentyLogSeverity } from './log-registry'

export type LentyConsoleLike = Pick<Console, 'error' | 'warn' | 'info'> & Partial<Pick<Console, 'debug'>>
export type LentyLogSink = (event: LentyLogEvent) => void

type LentyLogDispatch = (event: LentyLogEvent) => boolean
type LentyLogEmitOptions = { error?: unknown; sink?: LentyConsoleLike }

const LENTY_LOG_DISPATCH_KEY = '__LentyStyleDispatchLogEvent__'

// Format one stable user-facing log line for browser and node environments.
export function formatLentyLog(event: LentyLogEvent): string {
  const channelPrefix = event.channel === event.scope ? '' : `[${event.channel}] `
  return `[LentyStyle][${event.scope}][${event.severity}][${event.id}] ${channelPrefix}${event.message}`
}

// Resolve one log id and route it through the active dispatcher or console sink.
export function emitLentyLog(id: string, params: Record<string, unknown> = {}, options: LentyLogEmitOptions = {}): void {
  const event = resolveLentyLogEvent(id, params, { error: options.error })
  // Stop after the current environment-specific dispatcher handles this event.
  if (dispatchLentyLog(event)) {
    return
  }

  emitLentyConsoleLog(event, options.sink ?? console)
}

// Emit one formatted log event through the provided console-like sink.
export function emitLentyConsoleLog(event: LentyLogEvent, sink: LentyConsoleLike = console): void {
  const message = formatLentyLog(event)
  const method = resolveConsoleMethod(event.severity, sink)
  // Emit message only when the current event carries no error payload.
  if (event.error === undefined) {
    method(message)
    return
  }

  method(message, event.error)
}

// Build one reusable console-backed sink for repeated event emission.
export function createConsoleLentyLogSink(sink: LentyConsoleLike = console): LentyLogSink {
  return (event) => emitLentyConsoleLog(event, sink)
}

// Resolve the best console method for the requested severity.
function resolveConsoleMethod(severity: LentyLogSeverity, sink: LentyConsoleLike): (...data: unknown[]) => void {
  // Route error events to the strongest available console sink.
  if (severity === 'error') return sink.error ?? sink.warn ?? sink.info
  // Route warning events to warn or the nearest available fallback.
  if (severity === 'warn') return sink.warn ?? sink.info ?? sink.error
  // Route debug events to debug or the nearest available fallback.
  if (severity === 'debug') return sink.debug ?? sink.info ?? sink.warn ?? sink.error
  return sink.info ?? sink.warn ?? sink.error
}

// Route one resolved event into the runtime/browser dispatcher when available.
function dispatchLentyLog(event: LentyLogEvent): boolean {
  const runtimeGlobal = globalThis as typeof globalThis & { [LENTY_LOG_DISPATCH_KEY]?: LentyLogDispatch }
  const dispatch = runtimeGlobal[LENTY_LOG_DISPATCH_KEY]
  // Stop dispatch attempts when no environment-specific handler is installed.
  if (!dispatch) {
    return false
  }

  return dispatch(event) === true
}

export type { LentyLogChannel, LentyLogEvent, LentyLogScope, LentyLogSeverity } from './log-registry'
export { resolveLentyLogEvent } from './log-registry'
